import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, CirclePlusIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  AllUserListItem,
  allUserListQuery,
} from "@/app/Users/GraphQL/allUserListQuery";
import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";

export function UserFilter({
  placeholder,
  onChange,
  selected,
}: {
  placeholder: string;
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const numSelected = selected.length;
  const [filledValues, setFilledValues] = useState<AllUserListItem[]>([]);

  const labels = filledValues.map((user) => user.name);

  const selectedLabel =
    numSelected > 2
      ? `${labels[0]} and ${numSelected - 1} more`
      : labels.join(", ");

  const [search, setSearch] = useState("");

  const { data } = useGqlQuery(
    allUserListQuery,
    {
      search,
      first: 20,
      page: 1,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  return (
    <Popover>
      <PopoverTrigger className="inline-flex items-center space-x-1 rounded-full border border-dashed border-gray-300 px-2 py-px text-xs transition-all hover:border-gray-500 hover:bg-gray-100">
        <CirclePlusIcon
          onClick={(e) => {
            if (numSelected === 0) return;
            e.preventDefault();
            onChange([]);
            setFilledValues([]);
          }}
          className={cn("h-3 w-3 transition-all", {
            "rotate-0": numSelected === 0,
            "rotate-45 hover:text-red-500": numSelected > 0,
          })}
        />
        <span className="font-semibold text-gray-700">{placeholder}</span>

        {numSelected > 0 ? (
          <span className="border-l border-gray-300 px-1 font-semibold text-indigo-500">
            {selectedLabel}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search users..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {data?.allUsers.data.map((user) => {
                const isSelected = selected.includes(user.id);

                return (
                  <CommandItem
                    key={user.id}
                    value={user.id}
                    onSelect={(currentValue) => {
                      if (selected.includes(currentValue)) {
                        onChange(selected.filter((v) => v !== currentValue));
                        setFilledValues(
                          filledValues.filter((v) => v.id !== currentValue),
                        );
                      } else {
                        onChange([...selected, currentValue]);
                        setFilledValues([...filledValues, user]);
                      }
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div>
                      <p>{user.name}</p>
                      {user.primary_email ? (
                        <p className="text-xs text-gray-500">
                          {user.primary_email}
                        </p>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
